import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/useAuth';

// Signalements employés sur les cocktails / bouffe. Le proprio/admin les voit
// dans Prix de vente (points rouges + notification).

export type MenuItemType = 'cocktail' | 'food';

export interface MenuReport {
  id: string;
  item_type: MenuItemType;
  item_id: string;
  item_name: string;
  employee_id: string | null;
  message: string;
  status: 'open' | 'resolved';
  created_at: string;
  resolved_at: string | null;
  employee?: { first_name: string; last_name: string | null } | null;
}

export const MENU_REPORTS_KEY = ['menu-reports'] as const;
export const MENU_REPORTS_MAP_KEY = ['menu-reports', 'map'] as const;

/** Crée un signalement (employé). */
export function useCreateMenuReport() {
  const qc = useQueryClient();
  const { employee } = useAuth();
  return useMutation({
    mutationFn: async ({
      itemType,
      itemId,
      itemName,
      message,
    }: {
      itemType: MenuItemType;
      itemId: string;
      itemName: string;
      message: string;
    }): Promise<void> => {
      if (!employee) throw new Error('Aucune fiche employé liée à ton compte.');
      const { error } = await supabase.from('menu_item_reports').insert({
        item_type: itemType,
        item_id: itemId,
        item_name: itemName,
        message,
        employee_id: employee.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MENU_REPORTS_KEY });
    },
  });
}

/** Signalements ouverts (proprio/admin), filtrés par type au besoin. */
export function useOpenReports(type?: MenuItemType) {
  return useQuery({
    queryKey: [...MENU_REPORTS_KEY, 'open', type ?? 'all'] as const,
    queryFn: async (): Promise<MenuReport[]> => {
      let q = supabase
        .from('menu_item_reports')
        .select(
          '*, employee:employees(first_name, last_name)'
        )
        .eq('status', 'open')
        .order('created_at', { ascending: false });
      if (type) q = q.eq('item_type', type);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as MenuReport[];
    },
    staleTime: 30 * 1000,
  });
}

export interface OpenReportsMap {
  total: number;
  byItem: Map<string, number>; // clé `${type}:${id}`
}

/** Carte des items ayant des signalements ouverts (points rouges + total). */
export function useOpenReportMap(): OpenReportsMap {
  const { isAdminOrOwner } = useAuth();
  const query = useQuery({
    queryKey: MENU_REPORTS_MAP_KEY,
    enabled: isAdminOrOwner,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<OpenReportsMap> => {
      const { data, error } = await supabase
        .from('menu_item_reports')
        .select('item_type, item_id')
        .eq('status', 'open');
      if (error) throw error;
      const byItem = new Map<string, number>();
      for (const r of (data ?? []) as { item_type: string; item_id: string }[]) {
        const key = `${r.item_type}:${r.item_id}`;
        byItem.set(key, (byItem.get(key) ?? 0) + 1);
      }
      let total = 0;
      for (const v of byItem.values()) total += v;
      return { total, byItem };
    },
  });
  return query.data ?? { total: 0, byItem: new Map() };
}

/** Marque un signalement résolu (proprio/admin). */
export function useResolveReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from('menu_item_reports')
        .update({ status: 'resolved', resolved_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MENU_REPORTS_KEY });
    },
  });
}
